import { useCallback, useEffect, useRef } from 'react';

/**
 * `isMounted()` is false once the component has unmounted.
 *
 * For async loaders that set state after an `await`: check it after each
 * await and stop if it's false. Setting state on an unmounted component is a
 * no-op in the browser, but React reads `window.event` before it notices the
 * component is gone. In a test, a loader that resumes after jsdom is torn
 * down therefore throws "window is not defined", and CI failed on exactly
 * that (FlightDetailsModal.unmountSafety.test.jsx). Stopping early also
 * skips the requests a closed screen no longer needs.
 *
 * Starts true, so it's safe to call in any order relative to the effects that
 * start the loaders. It flips back to true on a remount (React StrictMode's
 * dev double-mount).
 */
export function useIsMounted() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  return useCallback(() => mounted.current, []);
}
